import { useEffect, useState, useMemo } from 'react'
import { useSearchParams, Link } from 'react-router-dom'
import { analytics } from '../lib/api'
import { useAuth } from '../contexts/AuthContext'

interface DashboardData {
  user: {
    email: string
    user_id: string
    role: string
    api_balance: number
    created_at: string | null
  }
  period_days: number
  overview: {
    total_events: number
    total_sessions: number
    total_ideas: number
    total_conversations: number
    total_messages: number
    user_messages: number
    comments_posted: number
    ideas_shared_with_user: number
    ideas_shared_by_user: number
  }
  daily_activity: Array<{
    date: string
    events: number
    sessions: number
    total_duration_ms: number
  }>
  event_breakdown: Record<string, number>
  page_breakdown: Record<string, number>
  top_events: Record<string, number>
  top_features: Array<{ feature: string; count: number }>
  page_engagement: Array<{ page: string; total_seconds: number; visits: number }>
  friction: {
    total: number
    by_type: Record<string, number>
    top_points: Array<{
      page: string
      event: string
      friction_type: string
      count: number
      unique_users: number
    }>
  }
  collaboration: {
    ideas_owned: number
    ideas_shared_with: number
    ideas_shared_by: number
    comments_posted: number
  }
}

interface LiveSession {
  session_id: string
  user_id: string
  email: string
  events: number
  current_page: string | null
  pages_visited: string[]
  first_event: string | null
  last_event: string | null
}

function StatCard({ label, value, color }: { label: string; value: string | number; color: string }) {
  const colors: Record<string, string> = {
    blue: 'border-blue-900/30 bg-blue-950/20 text-blue-400',
    green: 'border-emerald-900/30 bg-emerald-950/20 text-emerald-400',
    amber: 'border-amber-900/30 bg-amber-950/20 text-amber-400',
    red: 'border-red-900/30 bg-red-950/20 text-red-400',
    purple: 'border-purple-900/30 bg-purple-950/20 text-purple-400',
    teal: 'border-teal-900/30 bg-teal-950/20 text-teal-400',
  }
  return (
    <div className={`rounded-xl border p-4 ${colors[color] || colors.blue}`}>
      <span className="text-xs font-medium opacity-70">{label}</span>
      <div className="text-2xl font-bold mt-1">{value}</div>
    </div>
  )
}

function BarItem({ label, value, max }: { label: string; value: number; max: number }) {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0
  return (
    <div className="flex items-center gap-3">
      <span className="text-xs text-gray-400 w-28 truncate" title={label}>{label}</span>
      <div className="flex-1 bg-gray-800 rounded-full h-2 overflow-hidden">
        <div
          className="h-full bg-brand-500 rounded-full transition-all duration-500"
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className="text-xs font-medium text-gray-300 w-10 text-right">{value}</span>
    </div>
  )
}

export default function AnalyticsPage() {
  const { user } = useAuth()
  const [searchParams] = useSearchParams()
  const email = searchParams.get('email') || user?.email || ''
  const [days, setDays] = useState(30)
  const [data, setData] = useState<DashboardData | null>(null)
  const [liveSessions, setLiveSessions] = useState<LiveSession[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [refreshing, setRefreshing] = useState(false)

  const loadData = async () => {
    try {
      setRefreshing(true)
      const [dashData, live] = await Promise.allSettled([
        analytics.getUserDashboard(email, days),
        analytics.getLiveSessions(),
      ])
      if (dashData.status === 'fulfilled') {
        const raw = dashData.value as DashboardData
        if (!raw.friction) (raw as any).friction = { total: 0, by_type: {}, top_points: [] }
        if (!raw.overview) (raw as any).overview = { total_events: 0, total_sessions: 0, total_ideas: 0, total_conversations: 0, total_messages: 0, user_messages: 0, comments_posted: 0, ideas_shared_with_user: 0, ideas_shared_by_user: 0 }
        if (!raw.daily_activity) (raw as any).daily_activity = []
        if (!raw.top_features) (raw as any).top_features = []
        if (!raw.page_engagement) (raw as any).page_engagement = []
        if (!raw.collaboration) (raw as any).collaboration = { ideas_owned: 0, ideas_shared_with: 0, ideas_shared_by: 0, comments_posted: 0 }
        if (!raw.event_breakdown) (raw as any).event_breakdown = {}
        if (!raw.page_breakdown) (raw as any).page_breakdown = {}
        setData(raw)
      }
      if (live.status === 'fulfilled') {
        const liveData = live.value as { active_sessions: LiveSession[] }
        setLiveSessions(liveData.active_sessions || [])
      }
      setError('')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load analytics')
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }

  useEffect(() => {
    loadData()
    const interval = setInterval(loadData, 30_000)
    return () => clearInterval(interval)
  }, [email, days])

  const maxDailyEvents = useMemo(() => {
    if (!data?.daily_activity) return 0
    return Math.max(...data.daily_activity.map((d) => d.events), 1)
  }, [data?.daily_activity])

  if (user?.role !== 'admin') {
    return (
      <div className="flex-1 flex items-center justify-center">
        <p className="text-gray-500">Admin access required</p>
      </div>
    )
  }

  if (loading && !data) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-brand-500" />
          <p className="mt-3 text-gray-400 text-sm">Loading analytics...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="flex-1 overflow-y-auto px-4 py-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Link
              to="/admin"
              className="p-1.5 text-gray-400 hover:text-gray-200 hover:bg-gray-800 rounded transition"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </Link>
            <div>
              <h1 className="text-xl font-bold text-gray-100 flex items-center gap-2">
                <svg className="w-5 h-5 text-brand-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                User Analytics
              </h1>
              <p className="text-xs text-gray-500">{email}</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <select
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
              className="px-3 py-1.5 text-xs rounded-lg border border-gray-700 bg-gray-800 text-gray-200 outline-none focus:ring-1 focus:ring-brand-500"
            >
              <option value={7}>7 days</option>
              <option value={14}>14 days</option>
              <option value={30}>30 days</option>
              <option value={90}>90 days</option>
            </select>
            <button
              onClick={loadData}
              disabled={refreshing}
              className="p-1.5 rounded-lg hover:bg-gray-800 transition"
              title="Refresh"
            >
              <svg className={`w-4 h-4 text-gray-400 ${refreshing ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>
          </div>
        </div>

        {error && (
          <div className="bg-red-950 border border-red-800 rounded-lg p-4 mb-6">
            <p className="text-red-400 text-sm">{error}</p>
          </div>
        )}

        {data && (
          <div className="space-y-6">
            {/* Live Sessions */}
            {liveSessions.length > 0 && (
              <div className="bg-emerald-950/20 border border-emerald-900/30 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse" />
                  <span className="text-sm font-semibold text-emerald-300">
                    {liveSessions.length} Active Session{liveSessions.length !== 1 ? 's' : ''}
                  </span>
                </div>
                <div className="space-y-2">
                  {liveSessions.map((s) => (
                    <div key={s.session_id} className="flex items-center gap-3 text-xs">
                      <span className="font-medium text-gray-200 w-40 truncate">{s.email}</span>
                      <span className="text-emerald-400 w-24 truncate">{s.current_page || 'unknown'}</span>
                      <span className="text-gray-500">{s.events} events</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Overview Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              <StatCard label="Total Events" value={data.overview.total_events} color="blue" />
              <StatCard label="Sessions" value={data.overview.total_sessions} color="green" />
              <StatCard label="Ideas" value={data.overview.total_ideas} color="amber" />
              <StatCard label="Conversations" value={data.overview.total_conversations} color="purple" />
              <StatCard label="Messages Sent" value={data.overview.user_messages} color="teal" />
              <StatCard label="Friction" value={data.friction?.total ?? 0} color="red" />
            </div>

            {/* Daily Activity */}
            {data.daily_activity.length > 0 && (
              <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
                <h2 className="text-sm font-semibold text-gray-200 mb-4">Daily Activity</h2>
                <div className="flex items-end gap-1 h-32">
                  {data.daily_activity.map((day) => {
                    const pct = maxDailyEvents > 0 ? (day.events / maxDailyEvents) * 100 : 0
                    return (
                      <div key={day.date} className="flex-1 flex flex-col items-center gap-1 group">
                        <div className="w-full relative" style={{ height: '100px' }}>
                          <div
                            className="absolute bottom-0 w-full bg-brand-500 rounded-t transition-all duration-300 group-hover:bg-brand-400"
                            style={{ height: `${pct}%`, minHeight: pct > 0 ? '4px' : '0' }}
                            title={`${day.date}: ${day.events} events, ${day.sessions} sessions`}
                          />
                        </div>
                        <span className="text-[8px] text-gray-600 -rotate-45 origin-top-left">
                          {day.date.slice(5)}
                        </span>
                      </div>
                    )
                  })}
                </div>
              </div>
            )}

            <div className="grid md:grid-cols-2 gap-6">
              {/* Page Engagement */}
              {data.page_engagement.length > 0 && (
                <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
                  <h2 className="text-sm font-semibold text-gray-200 mb-4">Page Engagement (time)</h2>
                  <div className="space-y-2">
                    {data.page_engagement.slice(0, 8).map((pe) => (
                      <BarItem
                        key={pe.page}
                        label={pe.page}
                        value={pe.total_seconds}
                        max={data.page_engagement[0]?.total_seconds || 1}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Top Features */}
              {data.top_features.length > 0 && (
                <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
                  <h2 className="text-sm font-semibold text-gray-200 mb-4">Top Features</h2>
                  <div className="space-y-2">
                    {data.top_features.slice(0, 8).map((tf) => (
                      <BarItem
                        key={tf.feature}
                        label={tf.feature}
                        value={tf.count}
                        max={data.top_features[0]?.count || 1}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Event Types */}
              {Object.keys(data.event_breakdown).length > 0 && (
                <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
                  <h2 className="text-sm font-semibold text-gray-200 mb-4">Event Types</h2>
                  <div className="space-y-2">
                    {Object.entries(data.event_breakdown)
                      .sort(([, a], [, b]) => b - a)
                      .slice(0, 8)
                      .map(([type, count]) => (
                        <BarItem
                          key={type}
                          label={type}
                          value={count}
                          max={Math.max(...Object.values(data.event_breakdown))}
                        />
                      ))}
                  </div>
                </div>
              )}

              {/* Collaboration */}
              <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
                <h2 className="text-sm font-semibold text-gray-200 mb-4">Collaboration</h2>
                <div className="grid grid-cols-2 gap-4">
                  {[
                    { label: 'Ideas Created', value: data.collaboration.ideas_owned },
                    { label: 'Shared With', value: data.collaboration.ideas_shared_with },
                    { label: 'Shared By', value: data.collaboration.ideas_shared_by },
                    { label: 'Comments', value: data.collaboration.comments_posted },
                  ].map((item) => (
                    <div key={item.label} className="text-center p-3 bg-gray-800/50 rounded-lg">
                      <div className="text-2xl font-bold text-gray-100">{item.value}</div>
                      <div className="text-[10px] text-gray-500 mt-1">{item.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Friction Points */}
            {(data.friction?.total ?? 0) > 0 && (
              <div className="bg-gray-900 border border-red-900/30 rounded-xl p-5">
                <h2 className="text-sm font-semibold text-gray-200 mb-4">
                  Friction Points ({data.friction.total} total)
                </h2>
                {data.friction.top_points.length > 0 ? (
                  <div className="space-y-2">
                    {data.friction.top_points.map((fp, idx) => (
                      <div key={idx} className="flex items-center gap-3 px-3 py-2 bg-red-950/20 rounded-lg text-xs">
                        <span className="font-medium text-gray-200 w-24 truncate">{fp.page}</span>
                        <span className="text-red-400 w-24 truncate">{fp.friction_type}</span>
                        <span className="text-gray-500 flex-1 truncate">{fp.event}</span>
                        <span className="font-bold text-red-300">{fp.count}x</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-xs text-gray-500">
                    {Object.entries(data.friction.by_type).map(([type, count]) => (
                      <span key={type} className="inline-flex items-center gap-1 mr-3">
                        <span className="font-medium">{type}:</span> {count}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Page Visits */}
            {Object.keys(data.page_breakdown).length > 0 && (
              <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
                <h2 className="text-sm font-semibold text-gray-200 mb-4">Page Visits</h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
                  {Object.entries(data.page_breakdown)
                    .sort(([, a], [, b]) => b - a)
                    .map(([page, count]) => (
                      <div key={page} className="text-center p-2 bg-gray-800/50 rounded-lg">
                        <div className="text-lg font-bold text-gray-100">{count}</div>
                        <div className="text-[10px] text-gray-500 truncate">{page}</div>
                      </div>
                    ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
